import { DateTime } from 'luxon';
import { isEmpty as _isEmpty, compact } from 'lodash';
import Notify from 'quasar/src/plugins/Notify.js';;
import store from '../store/index';
import { parsePhoneNumber } from 'libphonenumber-js';
import { sentenceCase } from 'change-case';
import i18next from 'i18next';
import { resolveWellKnownEndpoint } from '../store/modules/well_known';
import { genericHttp } from './http';
import { helpers } from 'vuelidate/lib/validators';
import { Buffer } from 'buffer';

// CAUTION: store import won't work here

export function random(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

/**
 * Randomize array element order in-place.
 * Using Durstenfeld shuffle algorithm.
 */
export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    let temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
  return array;
};

/**
 * Get the status of a service
 * @param {{startTime: string, endTime: string, serviceDate: string}} options Options object
 * @returns {{status: 'done' | 'ongoing' | 'oncoming' | null, ongoing: boolean, oncoming: boolean, done: boolean}} The status
 */
export const getServiceStatus = function ({ startTime, endTime }) {
  let status;
  if (!startTime || !endTime) {
    status = null;
  } else {
    // Use the local system (user's browser) time to compute datetime
    const NOW = DateTime.local();
    const serviceStartTime = DateTime.fromISO(startTime);
    let serviceEndTime = DateTime.fromISO(endTime);
    if (serviceEndTime < serviceStartTime) {
      serviceEndTime = serviceEndTime.plus({ day: 1 });
    }

    // Check if NOW is greater than or equal to service start time
    // or if NOW is lesser than to service end time
    const lowerDiff = NOW.diff(serviceStartTime, 'seconds').toObject().seconds;
    const upperDiff = NOW.diff(serviceEndTime, 'seconds').toObject().seconds;

    status = lowerDiff < 0 ? 'oncoming' : lowerDiff >= 0 && upperDiff < 0 ? 'ongoing' : 'done';
  }

  return {
    status,
    ongoing: status === 'ongoing',
    oncoming: status === 'oncoming',
    done: status === 'done',
  };
};

/**
 * Compute service title
 * @param {string} serviceType Service Type
 * @param {string} serviceDate Service Date
 * @returns {string} The service title
 */
export function getServiceTitle(serviceType, serviceDate) {
  return `${serviceType} of ${DateTime.fromISO(serviceDate).toLocaleString(DateTime.DATE_FULL)}`;
}

export function getChurchLocaleDate(date, offset) {
  const dateTime = DateTime.fromISO(date);
  return offset
    ? dateTime.setZone(offset).toLocaleString(DateTime.DATE_FULL)
    : dateTime.toLocaleString(DateTime.DATE_FULL);
}

export function getChurchLocaleTime(time, offset) {
  const dateTime = DateTime.fromFormat(time, 'TT');
  return offset
    ? dateTime.setZone(offset).toLocaleString(DateTime.TIME_SIMPLE)
    : dateTime.toLocaleString(DateTime.TIME_SIMPLE);
}

export function getLocaleTimestamp(timestamp) {
  return timestamp
    ? DateTime.fromISO(timestamp).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)
    : '';
}

/**
 * Compute a full name
 * @param {object} name Name object
 * @param {string} name.firstName first Name
 * @param {string=} name.middleName middle  Name
 * @param {string} name.lastName last Name
 * @returns {string} The full name
 */
export function getFullName({ firstName, middleName, lastName }) {
  return compact([firstName, middleName, lastName]).join(' ');
}

/**
 * Reusable function for invoke and downloading a blob as file.
 *
 * @param {Blob} blob
 * @param {string} fileName
 */
export function invokeDownloadLink(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  // Clean up the URL object to avoid memory leaks
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Download file from API response
 *
 * @param {import('axios').AxiosResponse<ArrayBuffer>} response
 * @param {'roster' | 'report' | 'document' | 'file' | 'contact_card'} downloadType
 */
export function downloadFileFromResponse(response, downloadType = 'document') {
  return new Promise((resolve, reject) => {
    try {
      // Example header: `content-disposition: attachment;filename = 'screenshot_1_v1.0.0.png';
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'downloaded_file';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      invokeDownloadLink(blob, fileName);

      resolve();
    } catch (error) {
      Notify.create({
        message: i18next.t('problem_while_downloading_the_requested_download_type', {
          ns: 'toasts',
          context: downloadType,
        }),
        type: 'negative',
        timeout: 0,
      });
      reject(error);
    }
  });
}

/**
 * A function to get initials from a name
 * @param {string} name The name to get initials from
 * @returns {string} The initials
 */
export function getInitials(name) {
  const nameArray = name?.trim().split(' ');
  if (nameArray && Array.isArray(nameArray) && !!nameArray.length) {
    if (nameArray.length > 1) {
      return nameArray[0].charAt(0) + nameArray[nameArray.length - 1].charAt(0);
    }
    return nameArray[0].charAt(0) + nameArray[0].charAt(1);
  }
  return '';
}

/**
 *
 * @param {string} text
 */
export function capitalise(text) {
  if (typeof text !== 'string') {
    throw new SyntaxError('text should be a string');
  }
  return `${text.charAt(0).toLocaleUpperCase()}${text.slice(1)}`;
}

/**
 * Group a number in thousands with decimal support
 * @param {Number=} input Input number
 * @param {Number=} decimalPlaces Number of decimal places
 * @param {{code: string, exponent: number}=} useCurrency
 *
 * @returns String
 */
export function thousandGrouping(
  locale,
  input = 0,
  decimalPlaces = 2,
  useCurrency = {},
  currencyDisplay = 'code'
) {
  // If `decimalPlaces` is defined, we want to use it
  // As it can be used to override the currency's exponent
  const minimumFractionDigits = decimalPlaces ?? useCurrency.exponent;

  return input.toLocaleString(locale, {
    minimumFractionDigits,
    useGrouping: true,
    currency: useCurrency.code,
    style: useCurrency.code ? 'currency' : undefined,
    currencyDisplay,
  });
}

export function alphaSortFn(a, b) {
  if (a.toUpperCase() < b.toUpperCase()) return -1;
  if (a.toUpperCase() > b.toUpperCase()) return 1;
  else return 0;
}

export function dateTimeNormaliserLong(value) {
  if (value) {
    return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
  }
  return null;
}

export function normaliseText(text) {
  return typeof text === 'string' && !!text.trim()
    ? text
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    : '';
}

export function statusBadgeData(status, currentLocale) {
  if (!status) {
    return { colour: 'orange', status: i18next.t('none') };
  }

  let colour = 'green';
  let statusText = '';
  let locale = currentLocale ?? i18next.language;

  if (typeof status === 'object' && status.code) {
    statusText = status.localisations?.[locale]?.name ?? status.name;

    switch (status.code ?? status?.name) {
      case 'DSFLW':
        colour = 'red';
        break;
      case 'WEAK':
        colour = 'orange';
        break;
      case 'CHILD':
        colour = 'primary';
        break;
      case 'STDNT':
        colour = 'purple';
        break;
      case 'RELCT':
        colour = 'red';
        break;
      case 'ACTIV':
        colour = 'green';
        break;
      case 'HOLD':
        colour = 'orange';
        break;
      case 'TEMP':
        colour = 'purple';
        break;
      case 'TRAVL':
        colour = 'purple';
        break;
      case 'SLEPT':
        colour = 'dark';
        break;
      case 'SICK':
        colour = 'orange';
        break;
      case 'UNCRN':
        colour = 'orange';
        break;
      case 'MATERNITY':
        colour = 'indigo';
        break;
      case 'UNRCHBL':
        colour = 'orange';
        break;
      default:
        colour = 'purple';
        break;
    }
  } else {
    console.warn(`Warning: status must be an object with a "code" property. Received:`, status);
    throw new Error(
      `Invalid status: Expected an object with a "code" property, but got ${typeof status}.`
    );
  }

  return { colour, status: statusText, fullStatus: status };
}

export const ROLE_NAMES = {
  SUPERADMIN: 'Superadmin',
  DEVELOPER: 'Developer',
  SUPER_EDITOR: 'SuperEditor',
  ORG_ADMIN: 'OrgAdmin',
  ORG_EDITOR: 'OrgEditor',
  ORG_VIEWER: 'OrgViewer',
  SERVICE_ADMIN: 'ServiceAdmin',
  SERVICE_EDITOR: 'ServiceEditor',
  SERVICE_VIEWER: 'ServiceViewer',
  ZONAL_LEADER: 'ZonalLeader',
  MINISTRY_LEADER: 'MinistryLeader',
  MINISTRY_ASST_LEADER: 'MinistryAssistantLeader',
  GROUP_LEADER: 'GroupLeader',
  MEMBER: 'Member',
  FINANCIAL_BUDGET_APPROVER: 'FinancialBudgetApprover',
  FINANCIAL_REQUEST_APPROVER: 'FinancialRequestApprover',
  CHURCH_OWNER: 'Church Owner',
};

export const STATUSES = {
  DSFLW: 'Disfellowshipped Member',
  WEAK: 'Weak Member',
  RELCT: 'Relocated Member',
  ACTIV: 'Active Member',
  HOLD: 'Hold',
  TEMP: 'Temporary Member',
  TRAVL: 'Travelled Member',
  SLEPT: 'Slept',
  UNCRN: 'Uncertain',
  SICK: 'Sick Member',
  CHILD: 'A child',
  STDNT: 'Student Member',
  MATERNITY: 'On Maternity',
  UNRCHBL: 'Unreachable',
};

export function isEmpty(obj) {
  if (typeof obj === 'boolean') {
    return false;
  }

  if (!obj) {
    return true;
  }

  if (typeof obj === 'number') {
    return false;
  }

  if (!Array.isArray(obj) && typeof obj === 'object') {
    const isEmptyObject = Object.keys(obj).length === 0;
    if (isEmptyObject) {
      return true;
    }

    return Object.values(obj).every((value) => {
      return isEmpty(value);
    });
  }

  return _isEmpty(obj);
}

/**
 * @typedef {'small' | 'thumbnail' | 'large'} ImageFormat
 * @typedef {{extname: string; format: 'webp' | 'png' | 'jpeg' | 'gif'; hash: string; height: number; width: number; mimeType: string; name: string; size: number; url: string}} ImageProperties
 * @typedef {{breakpoints: Record<ImageFormat, ImageProperties>} & ImageProperties} ResponsiveImageData
 *
 * @param {ResponsiveImageData} imageData
 * @param {'small' | 'thumbnail' | 'large' | 'original'} breakpoint
 */
export function getRemoteResponsiveImage(imageData, breakpoint) {
  let resolvedUrl = '';
  if (breakpoint === 'original') {
    resolvedUrl = imageData?.url ?? '';
  }
  resolvedUrl = imageData?.breakpoints?.[breakpoint]?.url ?? imageData?.url ?? '';
  if (!resolvedUrl) {
    return resolvedUrl;
  }
  return resolvedUrl.startsWith('http')
    ? resolvedUrl
    : `${store.getters.getRootURL()}${resolvedUrl}`;
}

export function getRemoteResponsiveImageProps(imageData, breakpoint) {
  /**
   * @type {{url?: string; height?: number; width?: number}}
   */
  const imageProps = {
    url: undefined,
    height: undefined,
    width: undefined,
  };

  if (breakpoint === 'original') {
    imageData = imageData ?? null;
  } else {
    imageData = imageData?.breakpoints?.[breakpoint] ?? imageData ?? null;
  }

  if (!imageData) {
    return null;
  }

  imageProps.url = imageData.url;
  imageProps.height = imageData.height;
  imageProps.width = imageData.width;

  return imageProps;
}

export const strongPassRegex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/;

export const isLastItemInArray = (array, index) => index === array.length - 1;

export function isAsync(func) {
  return func.constructor && func.constructor.name === 'AsyncFunction';
}

/**
 * @param {[string, string]} options
 */
export const getBooleanSelectOptions = (options) => {
  return options.map((option, index) => ({ text: option, value: index === 0 }));
};

export function reportingPeriodOptions() {
  return [
    { text: '1 Month', value: 1 },
    { text: '2 Months', value: 2 },
    { text: '3 Months', value: 3 },
    { text: '6 Months', value: 6 },
    { text: '9 Months', value: 9 },
    { text: '12 Months', value: 12 },
    { text: '18 Months', value: 18 },
    { text: '24 Months', value: 24 },
  ];
}

/**
 * Get alert style object
 * @typedef {'info' | 'success' | 'danger' | 'warning'} Variant
 * @param {Variant} variant Variant
 */
export function alertStyle(variant) {
  /**
   * @param {Variant} variant
   */
  const alertBgColor = (variant) => {
    if (variant === 'info') {
      return 'bg-blue-2';
    }
    if (variant === 'warning') {
      return 'bg-orange-2';
    }
    if (variant === 'danger') {
      return 'bg-red-2';
    }
    if (variant === 'success') {
      return 'bg-green-2';
    }

    return 'bg-primary-3';
  };
  /**
   * @param {Variant} variant
   */
  const color = (variant) => {
    if (variant === 'info') {
      return 'blue-9';
    }
    if (variant === 'warning') {
      return 'orange-10';
    }
    if (variant === 'danger') {
      return 'red-10';
    }
    if (variant === 'success') {
      return 'green';
    }

    return 'primary';
  };
  /**
   * @param {Variant} variant
   */
  const textColor = (variant) => {
    if (variant === 'info') {
      return 'text-blue-10';
    }
    if (variant === 'warning') {
      return 'text-orange-10';
    }
    if (variant === 'danger') {
      return 'text-red-10';
    }
    if (variant === 'success') {
      return 'text-green-10';
    }

    return 'text-white';
  };

  return {
    color: color(variant),
    textColor: textColor(variant),
    alertBgColor: alertBgColor(variant),
  };
}

/**
 *
 * @param {number|size=} value
 * @returns
 */
export function sizePropValidator(value) {
  if (typeof value === 'number') {
    return true;
  }

  if (typeof value !== 'string') {
    console.warn('"size" prop should be a number or string');
    return false;
  }

  const validUnits = ['px', 'rem', 'em'];
  const standardQuasarSizes = ['xs', 'sm', 'md', 'lg', 'xl'];

  if (standardQuasarSizes.includes(value)) {
    return true;
  }

  if (!validUnits.some((unit) => value.toLowerCase().endsWith(unit))) {
    console.warn('"size" prop should end with "px" when it is a string');
  }

  return true;
}

/**
 *
 * @param {{loaded: number; total: number}} progressEvent
 */
export function downloadProgress(progressEvent) {
  const progress = progressEvent.loaded / progressEvent.total;
  store.commit('setDownloadProgress', progress);
}

/**
 *
 * @param {{loaded: number; total: number}} progressEvent
 */
export function uploadProgress(progressEvent) {
  const progress = progressEvent.loaded / progressEvent.total;
  store.commit('setUploadProgress', progress);
}

export const validPostTypes = ['text', 'images', 'videos', 'audios'];

/**
 * @param {number} timeout
 */
export const sleep = (timeout) => new Promise((resolve) => setTimeout(resolve, timeout));

// generate years
export const generateYears = () => {
  const maxAge = 70;
  return [...new Array(maxAge)]
    .map((_, index) => new Date().getFullYear() - maxAge + 1 + index)
    .map((year) => ({ value: year.toString(), text: year.toString() }));
};

export const urlRegex =
  /[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,10}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?/;

/**
 * Use this function to validate emails not just the regex alone
 *
 * @param {string} email
 */
export function isEmail(email) {
  const EMAIL_REGEX =
    /^[-!#$%&'*+\/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

  if (!email) {
    return false;
  }

  if (email.length > 254) {
    return false;
  }

  var valid = EMAIL_REGEX.test(email);
  if (!valid) {
    return false;
  }

  // Further checking of some things regex can't handle
  var parts = email.split('@');
  if (parts[0].length > 64) {
    return false;
  }

  var domainParts = parts[1].split('.');
  if (
    domainParts.some(function (part) {
      return part.length > 63;
    })
  ) {
    return false;
  }

  return true;
}

/**
 *
 * @param {any} value
 */
export function normaliseStringValue(value) {
  if (['boolean', 'number'].includes(typeof value)) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(normaliseStringValue);
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  const numberValue = +value;
  if (Number.isFinite(numberValue)) {
    return numberValue;
  }
  return value;
}

/**
 * Format phone number to international format
 *
 * @param {string|null|undefined} value
 * @param {string=} country
 * @returns {string}
 */
export function formatPhoneNumber(value, country) {
  if (!value) {
    return '';
  }
  // !Important: Return the value either way if it couldn't be parsed
  try {
    return parsePhoneNumber(value, country)?.formatInternational?.() ?? value;
  } catch {
    return value;
  }
}

export function serviceDateWithOrdinal(service) {
  if (!service) {
    return '';
  }
  return DateTime.fromISO(service.service_date).toLocaleString(DateTime.DATE_FULL);
}

export function serviceTitle(service) {
  if (!service) {
    return '';
  }
  const isServiceTypeOverridden = !!service.service_type_overridden;
  const serviceDate = serviceDateWithOrdinal(service);
  const serviceName = service.name;
  return isServiceTypeOverridden ? `${serviceName} (${serviceDate})` : serviceName;
}

// This should be only for English
export const GOTEDO_HANDLE_REGEX = /^[a-zA-Z][a-zA-Z0-9_]{2,19}$/;
export const RESOURCE_CODE_REGEX = /^[a-zA-Z0-9]+[a-zA-Z0-9-_]+/;

export function validGotedoHandleSegments(handle = '') {
  return {
    startsWithAlphabets: /^[a-zA-Z]/.test(handle),
    min3Max20Length: /^(.){3,20}$/.test(handle),
    containsValidCharacters: /[a-zA-Z0-9_]/.test(handle),
    overall: GOTEDO_HANDLE_REGEX.test(handle),
  };
}

export function getRouteLabel(item) {
  if (item?.meta?.label) {
    return item.meta.label;
  }
  return item?.name && typeof item.name === 'string' ? sentenceCase(item.name) : null;
}

/**
 * Return a DateTime instance created from either `fromSQL` or `fromISO`.
 * @param {string} dateTimeString
 */
export function normalisedDateTime(dateTimeString) {
  const isoDateTime = DateTime.fromISO(dateTimeString);
  return isoDateTime.isValid ? isoDateTime : DateTime.fromSQL(dateTimeString);
}

// message: i18next.t('problem_while_downloading_the_requested_download_type', {
//   ns: 'toasts',
//   downloadType,
// }),

export async function copyToClipboard({ text, successMessage, failureMessage }) {
  try {
    await navigator.clipboard.writeText(text);
    Notify.create({
      message: successMessage,
      color: 'positive',
      position: 'top',
    });
  } catch (error) {
    console.error('Failed to copy text to clipboard: ', error);
    Notify.create({
      message: failureMessage,
      color: 'negative',
      position: 'top',
    });
  }
}

/**
 * Recursively remove `null` and `undefined` keys from an object
 * @param {Record<string, any>} obj
 * @returns {Record<string, any>}
 */
export function compactObject(obj) {
  // Avoid passing `null` or `undefined` `into Object.entries`
  // Most likely happens when called recursively.
  if (!obj) {
    return obj;
  }

  const entries = Object.entries(obj)
    .map((entry) => {
      if (typeof entry[1] !== 'object' || Array.isArray(entry[1])) {
        return entry;
      }
      return [entry[0], compactObject(entry[1])];
    })
    .filter((entry) => {
      return entry[1] !== null && typeof entry[1] !== 'undefined';
    });

  return Object.fromEntries(entries);
}

/**
 * Computes the read time context based on the given read time and returns the translated string.
 *
 * @param {Object|null} readTime - The read time object.
 * @param {number} readTime.min - The number of minutes.
 * @param {number} readTime.sec - The number of seconds.
 * @returns {string|null} The translated read time string, or null if no read time is provided.
 */
export function translatedReadTime(readTime) {
  if (!readTime || typeof readTime.min !== 'number' || typeof readTime.sec !== 'number') {
    return null;
  }

  let context;
  let count;
  let min;

  if (readTime.min === 0) {
    context = 'second_only';
    count = readTime.sec;
  } else if (readTime.sec === 0) {
    context = 'minute_only';
    count = readTime.min;
  } else if (readTime.min === 1) {
    context = 'one_minute';
    count = readTime.sec;
  } else {
    context = 'minute';
    min = readTime.min;
    count = readTime.sec;
  }

  const translationKey = `product_documentations_module.read_time_${context}_${
    count === 1 ? 'one' : 'many'
  }`;

  return i18next.t(translationKey, {
    min,
    count,
    ns: 'gotedo_table_definitions',
  });
}

export function formatAsCurrency(input, localeCode, currency) {
  if (input == null || !localeCode || !currency) {
    return '';
  }

  const minimumFractionDigits = currency.exponent ?? 2;

  return input.toLocaleString(localeCode, {
    minimumFractionDigits,
    maximumFractionDigits: 2,
    currencyDisplay: 'narrowSymbol',
    style: 'currency',
    currency: currency.code,
    compactDisplay: 'short',
  });
}

/**
 * @param {number} n
 * @param {number=} digits
 */
export function roundTo(n, digits = 2) {
  var negative = false;
  if (digits === undefined) {
    digits = 0;
  }
  if (n < 0) {
    negative = true;
    n = n * -1;
  }
  var multiplicator = Math.pow(10, digits);
  n = parseFloat((n * multiplicator).toFixed(11));
  n = (Math.round(n) / multiplicator).toFixed(digits);
  if (negative) {
    n = (n * -1).toFixed(digits);
  }
  return n;
}

export function removeArticleBodyStyles() {
  /** @type {HTMLStyleElement} */
  const styleElement = document.querySelector('style#article-preview-styles');
  if (styleElement) {
    styleElement.remove();
  }
}

export async function injectArticleBodyStyles() {
  const payload = resolveWellKnownEndpoint('admin_api.v2.gotedo_docs.styles.article');

  if (!payload) {
    return;
  }

  await genericHttp({
    url: payload.url,
    method: payload.method,
    silent: true,
  }).then((response) => {
    const styles = response.data;

    if (!this.isPreviewPage) {
      const head = document.querySelector('head');
      const styleElement = document.createElement('style');
      styleElement.textContent = styles;
      styleElement.setAttribute('type', 'text/css');
      styleElement.setAttribute('id', 'article-preview-styles');
      head.appendChild(styleElement);
    } else {
      this.articlePreviewCss = styles;
    }
  });
}

/**
 * Placing the dash (-) at the end of the character class avoids
 * it being interpreted as a range indicator.
 * This ensures it's treated as a literal character.
 */
export const alphaNumRegex = helpers.regex('alphaNumRegex', /^\p{L}[\p{L}\p{N}\s'()&/"-]{3,}$/u);

//eslint-disable no-useless-escape
export const alphaNumValidationRegex = helpers.regex(
  'alphaNumValidationRegex',
  /^\p{L}{1}[\p{L}\p{N}\s]{3,}$/u
);

//eslint-disable no-useless-escape
export const alphaNumUnderScoreValidationRegex = helpers.regex(
  'alphaNumUnderScoreValidationRegex',
  /^\p{L}{1}[\p{L}\p{N}_-]{3,}$/u
);

//eslint-disable no-useless-escape
export const UUIDValidationRegex = helpers.regex(
  'UUIDValidationRegex',
  /\b(uuid:){0,1}\s*([a-f0-9\\-]*){1}\s*/
);

//eslint-disable no-useless-escape
export const dateValidationRegex = helpers.regex(
  'dateValidationRegex',
  /^[0-9]{4}[-]{0,1}[0-9]{2}[-]{0,1}[0-9]{2}$/
);

/**
 * @typedef {{length?: number; minLength?: number; maxLength?: number }} PkceCodeVerifierOptions
 */

/**
 * A cryptographically random string using the characters A-Z, a-z, 0-9,
 * and the punctuation characters -._~ (hyphen, period, underscore, and tilde),
 * between 43 and 128 characters long.
 *
 * @param {PkceCodeVerifierOptions=} options
 */
export function generatePkceCodeVerifier(options) {
  let { maxLength = 128, minLength = 100, length: requestedLength } = options || {};

  if (maxLength > 128) {
    maxLength = 128;
  }
  if (minLength < 100) {
    minLength = 100;
  }

  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const charactersLength = characters.length;

  const getRandomByte = () => {
    const array = new Uint8Array(1);
    window.crypto.getRandomValues(array);
    return array[0];
  };

  const length = requestedLength ?? (getRandomByte() % (maxLength - minLength + 1)) + minLength;

  let result = '';

  for (let i = 0; i < length; i++) {
    result += characters.charAt(getRandomByte() % charactersLength);
  }

  return result;
}

/**
 *
 * @param {string} message
 */
async function digestMessage(message) {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  return window.crypto.subtle.digest('SHA-256', data);
}

/**
 *
 * @param {ArrayBuffer} input
 */
export function base64UrlEncode(input) {
  const buffer = Buffer.from(input);
  const base64 = buffer.toString('base64');
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 *
 * @param {string} codeVerifier
 */
export async function hashPkceCodeVerifier(codeVerifier) {
  const hash = await digestMessage(codeVerifier);
  return base64UrlEncode(hash);
}
